import { defineMessages } from 'react-intl';

export const organisationDetailsStepMessages = defineMessages({
  cliniciansQuestion: {
    defaultMessage: 'How many clinicians do you work with?',
    id: 'eqNyIWlSzA',
    description: 'Question asking organisation size during setup',
  },
  justMe: {
    defaultMessage: 'Just me',
    id: 'VcXP8LG/3Q',
    description: 'Organisation size option for solo practice',
  },
  twoToFive: {
    defaultMessage: '2-5',
    id: 'rJgxocXxy+',
    description: 'Organisation size option for practices with 2 to 5 clinicians',
  },
  sixToTwenty: {
    defaultMessage: '6-20',
    id: 'AmUEjA8Dc5',
    description: 'Organisation size option for practices with 6 to 20 clinicians',
  },
  twentyOneToFifty: {
    defaultMessage: '21-50',
    id: 'Pm2+smossD',
    description: 'Organisation size option for practices with 21 to 50 clinicians',
  },
  fiftyOnePlus: {
    defaultMessage: '51+',
    id: 'Np/iQwHbQx',
    description: 'Organisation size option for practices with 51 or more clinicians',
  },
  continue: {
    defaultMessage: 'Continue',
    id: 'McmsgNSBzx',
    description: 'Primary action on organisation details setup step',
  },
  ehrEmrQuestion: {
    defaultMessage: 'Which EMR do you work with?',
    id: 'clYph5oHOm',
    description: 'Question asking which electronic medical record system is used',
  },
  ehrPlaceholderEmailOnly: {
    defaultMessage: 'None',
    id: 'BF1mbKFMru',
    description: 'Placeholder when no EHR is selected on email-only signup',
  },
  ehrSearchPlaceholder: {
    defaultMessage: 'Search…',
    id: 'pYoqwCpgbq',
    description: 'Search placeholder in EHR combobox during setup',
  },
  ehrNoResults: {
    defaultMessage: 'No results found',
    id: 'WwcLq4IN6S',
    description: 'Empty state when EHR search returns no matches',
  },
  ehrListLoading: {
    defaultMessage: 'Loading…',
    id: 'WDlGq4UTRi',
    description:
      'Shown inside the EMR combobox while the country-scoped EMR list is being fetched',
  },
  ehrListLoadError: {
    defaultMessage: "We couldn't load the EMR/EHR list.",
    id: '/geIv8b32E',
    description:
      'Error caption when the country-scoped EMR list fails to load on the organisation details survey step',
  },
  ehrListRetry: {
    defaultMessage: 'Try again',
    id: 'sCr8jS+qsd',
    description:
      'Retry action next to the EMR list load error on the organisation details survey step',
  },
  organisationName: {
    defaultMessage: 'Organisation name',
    id: 'pgOKFneUhD',
    description: 'Label for organisation name field during setup',
  },
  organisationNameUS: {
    defaultMessage: 'Organisation name',
    id: 'roxvl5Hj//',
    description: 'Label for organisation name field during US setup',
  },
  organisationNameUK: {
    defaultMessage: 'Organisation name (e.g. your clinic or trust)',
    id: 'WY/gDE9ZJv',
    description: 'Label for organisation name field during UK setup',
  },
  organisationNamePlaceholder: {
    defaultMessage: 'Please specify',
    id: 'OPET48C0Nl',
    description: 'Placeholder for organisation name input during setup',
  },
  // Single region-agnostic label + placeholder, shown when
  // `release-workspace-onboarding-rename` is ON.
  workspaceName: {
    defaultMessage: 'Workspace name',
    id: 'EuxmZDdUJP',
    description: 'Label for workspace name field during setup',
  },
  workspaceNamePlaceholder: {
    defaultMessage: 'e.g. Jane’s Workspace',
    id: 'G1z/CZCUQg',
    description: 'Placeholder for workspace name input during setup',
  },
  practiceFieldLabel: {
    defaultMessage: 'Which practice do you work at?',
    id: '1K1LWdcLsp',
    description: 'Label for UK CQC practice search field',
  },
  practiceSearchPlaceholder: {
    defaultMessage: 'Search for your practice',
    id: 'd0peiNhbO+',
    description: 'Helper placeholder for UK practice search autocomplete',
  },
  practiceSearchInputPlaceholder: {
    defaultMessage: 'Start typing…',
    id: 'yRJc8S2MR4',
    description: 'Input placeholder for UK practice search autocomplete',
  },
  practiceSearchNoResults: {
    defaultMessage: 'No practices found.',
    id: 'mYrP3kD28E',
    description: 'Empty state when UK practice search returns no matches',
  },
  stateRegion: {
    defaultMessage: 'State/Region',
    id: 'Vg4HHlWg+8',
    description: 'Label for state or region selector during onboarding',
  },
  sdrDemoOptIn: {
    defaultMessage: 'Request a 1:1 demo with a Heidi expert',
    id: 'R38Bm6DGUZ',
    description:
      'Checkbox label to opt in to requesting a 1:1 demo with a Heidi expert (request-demo variant)',
  },
  phoneNumber: {
    defaultMessage: 'Phone number',
    id: '3/ybzvG6tg',
    description: 'Label for phone number field on demo opt-in form',
  },
  phoneNumberPlaceholder: {
    defaultMessage: 'Enter a phone number',
    id: '4t5HoHkSfe',
    description: 'Placeholder for phone number field on demo opt-in form',
  },
  invalidPhoneNumber: {
    defaultMessage: 'Please enter a valid phone number',
    id: 'KJGAyTAHg5',
    description: 'Validation error when demo opt-in phone number is invalid',
  },
  demoReasonLabel: {
    defaultMessage: 'Reason for demo',
    id: 'XErIzT2iPg',
    description: 'Label for free-text reason field on demo opt-in form',
  },
  demoReasonPlaceholder: {
    defaultMessage: 'e.g. Exploring for my practice, evaluating AI tools…',
    id: 'OACAwvsepT',
    description: 'Placeholder for demo reason field on demo opt-in form',
  },
  bookDemoWebinarOptIn: {
    defaultMessage: 'Book a Demo / Webinar',
    id: '81x4T78dsb',
    description:
      'Checkbox label for the combined book-a-demo / webinar opt-in (webinar variant)',
  },
  webinarTimeslotLabel: {
    defaultMessage: 'Select a session time',
    id: '/m7ppGW+0v',
    description: 'Label for webinar session time dropdown',
  },
  webinarTimeslotPlaceholder: {
    defaultMessage: 'Select a session',
    id: 'CtSptwWz5e',
    description: 'Placeholder for webinar session time dropdown',
  },
  webinarTimeslotLoading: {
    defaultMessage: 'Loading sessions…',
    id: 'JM+UgemRse',
    description: 'Placeholder while webinar session times are loading',
  },
});
